import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import { $accessTokenFunc, $isAuthenticated } from "../authenication";
import { $docsApi } from "../docsapi/docsapi.atom";
import { type MembershipResponse, type UserWithMembership } from "../docsapi/docsapi.types";
import { type Workspace, isPersonalWorkspace } from "./workspace.types";

type WorkspaceMembersInput = {
	workspace: Workspace;
	userIds: string[];
};

const mapMembershipsToUsers = (membership: MembershipResponse): UserWithMembership[] => {
	const owners =
		membership.relation.owner?.reduce<UserWithMembership[]>((acc, userId) => {
			const user = membership.users[userId];
			if (user) {
				acc.push({
					...user,
					relation: "owner",
				});
			}
			return acc;
		}, []) ?? [];

	const members =
		membership.relation.member?.reduce<UserWithMembership[]>((acc, userId) => {
			const user = membership.users[userId];
			if (user) {
				acc.push({
					...user,
					relation: "member",
				});
			}
			return acc;
		}, []) ?? [];

	const invites =
		membership.invites?.map((invite) => ({
			name: invite,
			nickname: invite,
			user_id: invite,
			picture: undefined,
			relation: "member" as const,
		})) ?? [];

	return [...owners, ...members, ...invites];
};

export const $workspaceMembers = atomFamily((workspace: Workspace | undefined) => {
	const membersAtom = atomWithRefresh(async (get) => {
		if (!workspace) {
			return [] as UserWithMembership[];
		}

		if (!get($isAuthenticated)) {
			return [];
		}

		if (isPersonalWorkspace(workspace) || workspace.role === "guest") {
			return [];
		}

		const accessTokenFunc = get($accessTokenFunc);
		const token = await accessTokenFunc.getAccessTokenSilently?.();
		if (!token) {
			return [];
		}

		const docsApi = get($docsApi);
		const membership = await docsApi.workspaces.membership.query(workspace, token);
		return mapMembershipsToUsers(membership);
	});

	membersAtom.debugLabel = `workspaceMembers/${workspace?.id ?? "unknown"}`;
	return membersAtom;
}, isEqual);

export const $removeWorkspaceMembers = atom(
	null,
	async (get, set, { workspace, userIds }: WorkspaceMembersInput) => {
		if (!workspace || userIds.length === 0) {
			return;
		}

		if (isPersonalWorkspace(workspace) || workspace.role === "guest") {
			return;
		}

		if (!get($isAuthenticated)) {
			return;
		}

		const accessTokenFunc = get($accessTokenFunc);
		const token = await accessTokenFunc.getAccessTokenSilently?.();
		if (!token) {
			return;
		}

		const docsApi = get($docsApi);
		await docsApi.workspaces.membership.remove(workspace, userIds, token);

		//invalidate the workspace members atom since we just removed members
		set($workspaceMembers(workspace));
	}
);
$removeWorkspaceMembers.debugLabel = "removeWorkspaceMembers";

export const $inviteWorkspaceMembers = atom(
	null,
	async (get, set, { workspace, userIds }: WorkspaceMembersInput) => {
		if (!workspace || userIds.length === 0) {
			return;
		}

		if (!get($isAuthenticated)) {
			return;
		}

		const accessTokenFunc = get($accessTokenFunc);
		const token = await accessTokenFunc.getAccessTokenSilently?.();
		if (!token) {
			return;
		}

		const docsApi = get($docsApi);
		await docsApi.workspaces.membership.add(workspace, userIds, token);
		//invalidate the workspace members atom since we just invited members
		set($workspaceMembers(workspace));
	}
);
